/**
 * @fileoverview Family seats and the referral share, on the instructor's
 * Settings page (I12 + I13).
 *
 * One row, because they are one idea: decision D-S2's answer to "does
 * charging families cannibalise the instructor subscription?" — make the
 * instructor an ally. Seats to hand out, and a share when a family they
 * introduced subscribes.
 *
 * It lives under **Share Hans** beside the teacher referral rather than as a
 * card of its own, and the copy now says what a seat IS: a year of the Hans
 * family plan for a household — a parent login with the weekly digest,
 * practice reminders, goals and AI practice feedback. The old card said
 * "5 of them, to give to the families you teach" and nothing else, which is
 * how a founder came to ask what it was doing on the page.
 *
 * **It renders nothing until the family plan is live.** The endpoint 404s
 * while parent accounts are off, and the hook reads that as "unavailable" —
 * a seat is a year of something a parent has to be able to sign in to, and a
 * card promising five of a product that does not exist yet is worse than no
 * card.
 *
 * Two things the copy has to get right.
 *
 * **An instructor below the top tier sees WHY, not nothing.** A surface that
 * simply vanishes teaches nobody what the plan includes, which is the
 * opposite of what this row is for.
 *
 * **The share is stated as accrued, not owed.** Nothing here moves money —
 * a growing number with no explanation invites somebody to expect a transfer
 * that is not coming.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { SettingsRow } from "@/components/settings/SettingsSection";
import { Button } from "@/components/studio/primitives";
import { OFFERED_TIERS, highestTier, tierDisplayName } from "@/lib/tiers";

export function money(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

/** The plan that comes with seats: the top of the ladder we sell. */
const topPlanName = () => tierDisplayName(highestTier(OFFERED_TIERS));

/**
 * Loads what this instructor has to give and what they have earned.
 *
 * `seats` is `null` while loading, `false` when the surface is unavailable
 * (the 404 that means the family plan is not live, or any failure), and the
 * payload otherwise — so a caller can tell "not yet" from "not at all".
 */
export function useFamilySeats() {
  const [seats, setSeats] = useState(null);
  const [share, setShare] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/subscription/family-seats"),
        api.get("/subscription/family-referrals").catch(() => ({ data: null })),
      ]);
      // A payload with no seat count is not a seat surface — an empty 200
      // from a stub or a proxy must read as unavailable, not as a row with
      // "undefined of undefined left".
      if (typeof a.data?.total !== "number") {
        setSeats(false);
        return;
      }
      setSeats(a.data);
      setShare(b.data);
    } catch {
      setSeats(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { seats, share, reload };
}

/**
 * The row itself. Presentational; the data comes from `useFamilySeats`.
 *
 * @param {object} props
 * @param {object} props.seats The `/subscription/family-seats` payload.
 * @param {object|null} props.share The `/subscription/family-referrals` statement.
 * @param {function} props.reload Re-fetch after a give or a take-back.
 * @param {boolean} [props.last] Whether this is the card's final row.
 */
export function FamilySeatsRow({ seats, share, reload, last = true }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState(null);

  const give = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      await api.post("/subscription/family-seats", { email });
      setEmail("");
      await reload();
      toast.success("Seat sent");
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Rendered in place: an instructor who has run out needs to read what
      // to do about it, which a toast that vanishes cannot tell them.
      if (detail?.code) setRefusal(detail.message);
      else toast.error(getErrorMessage(err, "Couldn't send that seat"));
    } finally {
      setBusy(false);
    }
  };

  const takeBack = async (giftId) => {
    try {
      await api.delete(`/subscription/family-seats/${giftId}`);
      await reload();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't take that back"));
    }
  };

  const plan = topPlanName();

  if (!seats.eligible) {
    return (
      <SettingsRow
        testId="family-seats"
        title="Family seats"
        desc={
          <span data-testid="family-seats-locked">
            Family seats come with the top plan, {plan} — {seats.total} of them, to give to the families you teach: a
            year of the Hans family plan each, with a parent login, the weekly digest, practice reminders and goals.
          </span>
        }
        last={last}
      />
    );
  }

  return (
    <SettingsRow
      testId="family-seats"
      title={
        <span className="inline-flex flex-wrap items-baseline gap-2">
          Family seats
          <span className="font-mono text-[12px] font-normal text-[var(--text-tertiary)]">
            {seats.remaining} of {seats.total} left
          </span>
        </span>
      }
      desc={
        <>
          Included with {plan}: give a family a year of the Hans family plan, on you — a parent login with the weekly
          digest, practice reminders, goals and AI feedback on practice recordings.
          {share?.share_percent
            ? ` If a family you introduce subscribes on their own later, you earn ${share.share_percent}% of what they pay.`
            : ""}
        </>
      }
      align="start"
      last={last}
    >
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Parent's email"
          data-testid="family-seat-email"
          className="min-w-[220px] flex-1 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-4 py-2 font-sans text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]"
        />
        <Button onPress={give} variant="ghost" className="px-4 py-2 text-[13px]" disabled={busy || !email.trim()} data-testid="family-seat-give">
          Give a seat
        </Button>
      </div>

      {refusal ? (
        <p className="mt-2 font-sans text-[13px] text-[var(--text-secondary)]" data-testid="family-seat-refusal">
          {refusal}
        </p>
      ) : null}

      {seats.gifts?.length ? (
        <ul className="mt-3 divide-y divide-[var(--studio-divider)] rounded-xl border border-[var(--studio-divider)]">
          {seats.gifts.map((g) => (
            <li key={g.gift_id} className="flex items-center justify-between px-4 py-2.5">
              <span className="font-sans text-[13px] text-[var(--text-secondary)]">{g.email}</span>
              <button
                type="button"
                onClick={() => takeBack(g.gift_id)}
                data-testid={`family-seat-revoke-${g.gift_id}`}
                className="font-sans text-[12px] text-[var(--text-tertiary)] hover:underline"
              >
                Take back
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {share && share.balance_cents > 0 ? (
        <p className="mt-3 font-sans text-[12px] text-[var(--text-tertiary)]" data-testid="family-share">
          You&rsquo;ve earned {money(share.balance_cents)} from families you introduced ({share.share_percent}% of what
          they pay). We&rsquo;ll be in touch about paying it out.
        </p>
      ) : null}
    </SettingsRow>
  );
}

/**
 * The self-contained form: loads its own data and renders nothing while the
 * surface is unavailable. Settings composes the row through `useFamilySeats`
 * instead, so the section around it can decide whether it has anything to
 * show; this stays for anything that wants the row on its own.
 */
export function FamilySeatsCard() {
  const family = useFamilySeats();
  if (!family.seats) return null;
  return <FamilySeatsRow {...family} />;
}

export default FamilySeatsCard;
